import api from 'services/api';
import { REJECT, array, bool, nullable, numeric, object, str, type Received } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// GET /events/live — the whole Live Events page in one read. Backend:
// routes/events.js delegates to modules/generic_functions/events/getLiveEvents.js,
// which aggregates supply, demand and subscriptions per event.
//
// Nothing here is shared with the other events endpoints. The event columns
// coincide with those of `GET /events/:id`, but this is its own SQL statement
// aggregating three sources, so the shapes are equal-by-coincidence and are
// declared in full (see ../../README.md).

/**
 * A node instance the event has as a consumer.
 *
 * `is_waiting` is the engine's own definition of a waiter — an unsatisfied
 * wait row on an instance in `eventWait` — and only those count as demand.
 * Rows with it false are consumers the caller asked to keep visible via
 * `include_node_instance_ids`: ones a raise has already served, carried along
 * so the operator can see what the raise did. Their `status` is re-read on
 * every request, so they stay live rather than frozen at the moment they
 * stopped waiting.
 *
 * It can also arrive NULL, which is three-valued logic rather than a missing
 * value: the backend computes it as `nie.is_satisfied IS DISTINCT FROM true AND
 * ni.status = 'eventWait'`, and `ni.status` is a nullable column, so an
 * unsatisfied wait on a status-less instance evaluates `TRUE AND NULL` = NULL.
 * Such a row still reaches the result through the CTE's
 * `OR nie.node_instance_id = ANY(...)` branch — i.e. exactly the kept-visible
 * consumers above. A null reads as not-waiting downstream, which is what
 * `bool()` already produced; it is declared honestly so #1220 does not refuse
 * the row.
 */
export interface LiveEventWaitingInstance {
  readonly node_instance_id: number;
  readonly node_definition_id: number | null;
  readonly node_name: string | null;
  readonly auto_clear: boolean | null;
  readonly precondition: string | null;
  readonly scheduling_window_id: number | null;
  readonly status: string | null;
  readonly scheduled_start_datetime: string | null;
  readonly start_datetime: string | null;
  readonly is_waiting: boolean | null;
}

/**
 * A node definition the backend would auto-submit when the event is raised.
 * Present only when the caller asked for auto-submit to count as demand; the
 * endpoint withholds these entirely otherwise, so an empty array means either
 * "none" or "not requested" and the caller already knows which.
 *
 * Unlike a consumer, a subscription is never drawn down: a raise submits an
 * instance and the subscription stays. The list changes only when the node
 * definition is edited, so the newest read is always the whole truth — there
 * is nothing to carry forward.
 */
export interface LiveEventSubscription {
  readonly node_definition_id: number;
  readonly node_name: string | null;
  readonly precondition: string | null;
  readonly auto_submit_mode: string | null;
  readonly auto_submit_timezone: string | null;
}

export interface LiveEvent {
  readonly event_id: number;
  readonly name: string;
  readonly description: string | null;
  readonly type: string;
  readonly zone_id: number;
  readonly zone_name: string;
  readonly folder_id: number | null;
  readonly surplus_count: number;
  readonly waiting_count: number;
  readonly subscription_count: number;
  readonly waiting_instances: readonly LiveEventWaitingInstance[];
  /**
   * The event has more consumers than `waiting_instances` carries.
   *
   * `waiting_count` remains the true demand either way, so a card can report
   * the number honestly while showing only part of the list.
   */
  readonly consumers_truncated: boolean;
  readonly auto_submit_subscriptions: readonly LiveEventSubscription[];
  /**
   * False for an event returned only because `event_ids` asked for it — a
   * focused or just-refreshed card that is no longer out of balance. The page
   * marks those, so an event with nothing outstanding never looks like it
   * qualified on its own.
   */
  readonly qualifies: boolean;
  /**
   * When the database produced this row.
   *
   * Lets a holder of two reads of one event — a page load and its own card
   * refresh — decide which is fresher without coordinating clocks or tracking
   * request order.
   */
  readonly as_of: string;
}

export interface LiveEventsResponse {
  readonly events: readonly LiveEvent[];
  /** The row cap was hit; the page must say so rather than imply completeness. */
  readonly truncated: boolean;
  readonly limit: number;
}

const waitingInstanceSchema = object<LiveEventWaitingInstance>({
  node_instance_id: numeric(),
  node_definition_id: nullable(numeric()),
  node_name: nullable(str()),
  auto_clear: nullable(bool()),
  precondition: nullable(str()),
  scheduling_window_id: nullable(numeric()),
  status: nullable(str()),
  scheduled_start_datetime: nullable(str()),
  start_datetime: nullable(str()),
  is_waiting: nullable(bool()),
});

const subscriptionSchema = object<LiveEventSubscription>({
  node_definition_id: numeric(),
  node_name: nullable(str()),
  precondition: nullable(str()),
  auto_submit_mode: nullable(str()),
  auto_submit_timezone: nullable(str()),
});

const liveEventSchema = object<LiveEvent>({
  event_id: numeric(REJECT),
  name: str(),
  description: nullable(str()),
  type: str(),
  zone_id: numeric(),
  zone_name: str(),
  folder_id: nullable(numeric()),
  // Not an identity, but the card is derived entirely from these three: the count it
  // shows, whether it shows as qualifying, and the instant all of that was true at. A
  // substituted 0 / false / '' is a confident lie about live state, so the row is not a
  // row without them. (`enforce` said the same with a `[*]` drop-row directive.)
  surplus_count: numeric(REJECT),
  waiting_count: numeric(),
  subscription_count: numeric(),
  waiting_instances: array(waitingInstanceSchema),
  consumers_truncated: bool(),
  auto_submit_subscriptions: array(subscriptionSchema),
  qualifies: bool(REJECT),
  as_of: str(REJECT),
});

const responseSchema = object<LiveEventsResponse>({
  events: array(liveEventSchema),
  truncated: bool(),
  limit: numeric(),
});

/**
 * The most consumers one event carries, and the most the endpoint accepts in
 * `include_node_instance_ids` — the backend uses one bound for both directions
 * so that whatever a card was given can always be sent back. Exceeding it on
 * the way in is a 400, so a caller echoing a list must cap it here too.
 */
export const MAX_CARRIED_CONSUMERS = 500;

export interface LiveEventsParams {
  readonly zone_id?: number;
  readonly search?: string;
  readonly include_auto_submit?: boolean;
  /** Returned whether or not they qualify — serves both focus and per-card refresh. */
  readonly event_ids?: readonly number[];
  /**
   * Consumers to keep among the results even once they have stopped waiting,
   * each carrying its current status. A card re-reading itself passes the
   * consumers it is already showing. Capped at MAX_CARRIED_CONSUMERS.
   */
  readonly include_node_instance_ids?: readonly number[];
}

/**
 * GET `/events/live`. Throws on transport error only. A response that fails
 * validation is returned as an `unreadable` result rather than thrown, and the
 * caller decides what that costs (see `readResponse`).
 *
 * Every enforced path drops the offending card rather than failing the call:
 * one untrustworthy card should not cost the operator the rest of the page.
 * They are the fields the page's claims rest on — `event_id` addresses every
 * action, `surplus_count` is half of "what needs attention", `qualifies`
 * decides whether the card is marked force-visible, and `as_of` decides which
 * of two reads of an event wins. A card defaulted to id 0, surplus 0,
 * qualifies-false or an empty `as_of` would quietly misreport.
 */
export const getLiveEvents = async (params: LiveEventsParams = {}): Promise<Received<LiveEventsResponse>> => {
  const query: Record<string, unknown> = {};
  if (params.zone_id !== undefined) query.zone_id = params.zone_id;
  if (params.search) query.search = params.search;
  if (params.include_auto_submit) query.include_auto_submit = 'true';
  if (params.event_ids?.length) query.event_ids = params.event_ids.join(',');
  if (params.include_node_instance_ids?.length) query.include_node_instance_ids = params.include_node_instance_ids.join(',');

  const res = await api.get('/events/live', { params: query });
  return readResponse(res.data, {
    schema: responseSchema,
    source: 'getLiveEvents',
  });
};
